import "../../styles/chatbot.css";
import { useEffect, useState, useContext } from "react";
import { useLanguage } from "../../context/LanguageContext";
import api from "../../utils/api";
import chatbotAvatar from "../../assets/Anna_Chat_Avatar.png";
import chatloading from "../../assets/chatbot-loading.gif";
import { ChatbotContext } from "../../context/ChatbotContext";

const RecommendedCoursesMessage = ({
  speaks,
  // For RIASEC, pass an object of arrays (keys = category names)
  riasecCourses = {},
  // For STRAND-based recommendations, pass an array
  recommendedCourses = [],
  handleMessagesScrollToBottom,
  dialogflowEventQuery,
  setTextMessage,
  basis, // "riasec" or "strand"
  strand,
  language,
  riasecCode = [], // e.g. [['artistic', 6], ['conventional', 5], ['realistic', 4]]
}) => {
  const { language: contextLanguage } = useLanguage();
  const selectedLanguage = language || contextLanguage || "en";
  const [strandEventSent, setStrandEventSent] = useState(false);
  const [hasDisplayedMessage, setHasDisplayedMessage] = useState(false);

  const {
    isRecommendationProvided,
    setIsRecommendationProvided,
    setDisabledInput,
    setIsVisibleInput,
  } = useContext(ChatbotContext);

  const [isRecommendationLoading, setIsRecommendationLoading] = useState(false);
  const [showAllCourses, setShowAllCourses] = useState(false);
  const [studentStrand] = useState(strand);
  const [popularCache, setPopularCache] = useState({}); // { key: { order, counts, total } }

  // Proactively prefetch popularity ordering so courses don't lag behind headings
  useEffect(() => {
    const prefetch = async () => {
      try {
        if (basis === "riasec") {
          // Determine display order for categories
          const categoryOrder =
            riasecCode && riasecCode.length > 0
              ? riasecCode.map(([cat]) => cat.toLowerCase())
              : Object.keys(riasecCourses || {});

          // Prefetch only what will be shown initially (top category 4, others 3)
          categoryOrder.forEach((category, idx) => {
            const courses = riasecCourses?.[category] || [];
            if (!courses.length) return;
            const isTopCategory = idx === 0;
            const coursesToShow = isTopCategory ? 4 : 3;
            const displayed = courses.slice(0, coursesToShow);
            const names = displayed.map((c) =>
              typeof c === "string" ? c : c.name || c.stringValue || "",
            );
            const cacheKey = `riasec:${category}:${names.join("|")}`;
            if (!popularCache[cacheKey] && names.length) {
              api
                .post("/api/sort-courses-by-popularity/", {
                  courses: names,
                  category: "riasec",
                })
                .then((resp) => {
                  const order = resp?.data?.sorted_courses;
                  const counts = resp?.data?.counts || {};
                  const total = resp?.data?.unique_users_total || 0;
                  setPopularCache((prev) => ({
                    ...prev,
                    [cacheKey]: {
                      order: order && order.length ? order : names,
                      counts,
                      total,
                    },
                  }));
                })
                .catch(() =>
                  setPopularCache((prev) => ({
                    ...prev,
                    [cacheKey]: { order: names, counts: {}, total: 0 },
                  })),
                );
            }
          });
        } else if (basis === "strand") {
          const courses = showAllCourses
            ? recommendedCourses
            : recommendedCourses.slice(0, 5);
          const names = courses.map((c) =>
            typeof c === "string" ? c : c.name || c.stringValue || "",
          );
          const cacheKey = `strand:${names.join("|")}`;
          if (!popularCache[cacheKey] && names.length) {
            api
              .post("/api/sort-courses-by-popularity/", {
                courses: names,
                category: "strand",
              })
              .then((resp) => {
                const order = resp?.data?.sorted_courses;
                const counts = resp?.data?.counts || {};
                const total = resp?.data?.unique_users_total || 0;
                setPopularCache((prev) => ({
                  ...prev,
                  [cacheKey]: {
                    order: order && order.length ? order : names,
                    counts,
                    total,
                  },
                }));
              })
              .catch(() =>
                setPopularCache((prev) => ({
                  ...prev,
                  [cacheKey]: { order: names, counts: {}, total: 0 },
                })),
              );
          }
        }
      } catch {}
    };
    prefetch();
  }, [basis, riasecCode, riasecCourses, recommendedCourses, showAllCourses]);

  // Introductory text explaining it’s just a preview
  const introductoryText = {
    en: {
      riasec:
        "Based on your interests, here’s a preview of some degree programs that may suit you in college. They are grouped by your strongest RIASEC categories. Let me know if you’d like more details!",
      strand: `Since you’re from the ${studentStrand} strand, here’s a preview of recommended degree programs. Let me know if you need more suggestions!`,
    },
    fil: {
      riasec:
        "Batay sa iyong interes, narito ang isang preview ng mga kursong maaaring angkop sa iyo. Pinangkat ang mga ito ayon sa iyong pinakamalakas na RIASEC na mga kategorya. Ipaalam sa akin kung gusto mo pang makita ang iba pang detalye!",
      strand: `Dahil ikaw ay mula sa strand na ${studentStrand}, narito ang isang preview ng mga inirerekomendang kurso. Ipaalam sa akin kung kailangan mo pa ng iba pang mungkahi!`,
    },
  };

  useEffect(() => {
    if (
      !hasDisplayedMessage &&
      (isRecommendationProvided.riasec === "done" ||
        isRecommendationProvided.strand === "done") &&
      !isRecommendationLoading
    ) {
      setIsRecommendationLoading(true);
      setDisabledInput(true);
      setIsVisibleInput(false);
      setTextMessage("");

      setTimeout(() => {
        setIsRecommendationLoading(false);
        setDisabledInput(false);
        setIsRecommendationProvided((prev) => ({
          ...prev,
          riasec: prev.riasec === "done" ? "" : prev.riasec,
          strand: prev.strand === "done" ? "" : prev.strand,
        }));
        setHasDisplayedMessage(true);

        if (isRecommendationProvided.riasec === "done") {
          handleMessagesScrollToBottom();
          dialogflowEventQuery("ISLEARN_RIASEC_RECOMMENDED_COURSES");
        } else if (isRecommendationProvided.strand === "done") {
          handleMessagesScrollToBottom();
          dialogflowEventQuery("ISLEARN_STRAND_RECOMMENDED_COURSES");
        }
      }, 1000);
    }
  }, [isRecommendationProvided, isRecommendationLoading, hasDisplayedMessage]);

  // Once the message is displayed, hide the duplicate message from the UI

  if (basis === "riasec") {
    // For RIASEC, we assume riasecCourses is an object like:
    // { artistic: [...], conventional: [...], realistic: [...] }
    // To preserve the order (if available) from your riasecCode array, map over that:
    let categoryOrder = [];
    if (riasecCode && riasecCode.length > 0) {
      categoryOrder = riasecCode.map(([cat]) => cat.toLowerCase());
    } else {
      categoryOrder = Object.keys(riasecCourses);
    }

    return (
      <>
        {!isRecommendationLoading ? (
          <div className={`message ${speaks === "bot" ? "bot" : "user"}`}>
            {speaks === "bot" && (
              <div>
                <img
                  className="chatbot-avatar message-avatar"
                  src={chatbotAvatar}
                  alt="chathead"
                />
              </div>
            )}
            <div
              className={`message-text ${speaks === "bot" ? "bot" : "user"}`}
            >
              <p>
                {introductoryText[selectedLanguage]?.riasec ||
                  introductoryText.en.riasec}
              </p>
              {categoryOrder.map((category, idx) => {
                const courses = riasecCourses[category] || [];
                if (!courses.length) return null;

                // Determine how many courses to show: 4 for top category, 3 for the rest
                const isTopCategory = idx === 0;
                const coursesToShow = isTopCategory ? 4 : 3;
                let displayedCourses = showAllCourses
                  ? courses
                  : courses.slice(0, coursesToShow);
                const names = displayedCourses.map((c) =>
                  typeof c === "string" ? c : c.name || c.stringValue || "",
                );
                const cacheKey = `riasec:${category}:${names.join("|")}`;
                const cached = popularCache[cacheKey];
                if (!cached) {
                  api
                    .post("/api/sort-courses-by-popularity/", {
                      courses: names,
                      category: "riasec",
                    })
                    .then((resp) => {
                      const order = resp?.data?.sorted_courses;
                      const counts = resp?.data?.counts || {};
                      const total = resp?.data?.unique_users_total || 0;
                      setPopularCache((prev) => ({
                        ...prev,
                        [cacheKey]: {
                          order: order && order.length ? order : names,
                          counts,
                          total,
                        },
                      }));
                    })
                    .catch(() =>
                      setPopularCache((prev) => ({
                        ...prev,
                        [cacheKey]: { order: names, counts: {}, total: 0 },
                      })),
                    );
                }
                const orderedNames = popularCache[cacheKey]?.order || names;
                displayedCourses = orderedNames.map(
                  (n) =>
                    displayedCourses.find(
                      (c) =>
                        (typeof c === "string"
                          ? c
                          : c.name || c.stringValue || "") === n,
                    ) || n,
                );

                return (
                  <div key={idx}>
                    <h5 className="text-primary">{category.toUpperCase()}</h5>
                    <ul className="recommendedCourses">
                      {displayedCourses.map((courseObj, i) => {
                        const courseName =
                          typeof courseObj === "string"
                            ? courseObj
                            : courseObj.name || courseObj.stringValue || "";
                        return <li key={i}>{courseName}</li>;
                      })}
                    </ul>
                  </div>
                );
              })}
              <p className="small text-muted mt-2 recommendation-note">
                {selectedLanguage === "fil"
                  ? '📌 Mga halimbawang kurso sa bawat kategorya. Para makita ang kumpletong listahan, piliin ang "Yes" sa ibaba. Maaari mo ring pindutin ang kurso sa itaas para sa detalye.'
                  : '📌 Preview of courses within each category. To see the complete list, choose "Yes" below.'}
              </p>
            </div>
          </div>
        ) : (
          <div className="message bot">
            <div>
              <img
                className="chatbot-avatar message-avatar"
                src={chatbotAvatar}
                alt="chathead"
              />
            </div>
            <div className="message-text bot">
              <img
                className="message-loading"
                src={chatloading}
                alt="loading"
              />
            </div>
          </div>
        )}
      </>
    );
  } else {
    // For strand-based recommendations
    let coursesToDisplayForStrand = showAllCourses
      ? recommendedCourses
      : recommendedCourses.slice(0, 5);
    const strandNames = coursesToDisplayForStrand.map((c) =>
      typeof c === "string" ? c : c.name || c.stringValue || "",
    );
    const strandKey = `strand:${strandNames.join("|")}`;
    if (!popularCache[strandKey]) {
      api
        .post("/api/sort-courses-by-popularity/", {
          courses: strandNames,
          category: "strand",
        })
        .then((resp) => {
          const order = resp?.data?.sorted_courses;
          const counts = resp?.data?.counts || {};
          const total = resp?.data?.unique_users_total || 0;
          setPopularCache((prev) => ({
            ...prev,
            [strandKey]: {
              order: order && order.length ? order : strandNames,
              counts,
              total,
            },
          }));
        })
        .catch(() =>
          setPopularCache((prev) => ({
            ...prev,
            [strandKey]: { order: strandNames, counts: {}, total: 0 },
          })),
        );
    }
    // Sort strand courses: 1st priority = offered courses, 2nd priority = popularity
    const sortedStrandCourses = coursesToDisplayForStrand.sort((a, b) => {
      const aName = typeof a === "string" ? a : a.name || a.stringValue || "";
      const bName = typeof b === "string" ? b : b.name || b.stringValue || "";
      
      // Check if courses are offered (need to get offerings data)
      // For now, we'll just use popularity order since we don't have offerings in this component
      const popularityOrder = popularCache[strandKey]?.order || strandNames;
      const aIndex = popularityOrder.indexOf(aName);
      const bIndex = popularityOrder.indexOf(bName);
      
      if (aIndex === -1 && bIndex === -1) return 0;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      
      return aIndex - bIndex;
    });
    
    coursesToDisplayForStrand = sortedStrandCourses;
    return (
      <>
        {!isRecommendationLoading ? (
          <div className={`message ${speaks === "bot" ? "bot" : "user"}`}>
            {speaks === "bot" && (
              <div>
                <img
                  className="chatbot-avatar message-avatar"
                  src={chatbotAvatar}
                  alt="chathead"
                />
              </div>
            )}
            <div
              className={`message-text ${speaks === "bot" ? "bot" : "user"}`}
            >
              <p>
                {introductoryText[selectedLanguage]?.strand ||
                  introductoryText.en.strand}
              </p>
              <ul className="recommendedCourses">
                {coursesToDisplayForStrand.map((course, idx) => {
                  const courseName =
                    typeof course === "object"
                      ? course.name || course.stringValue
                      : course;
                  return <li key={idx}>{courseName}</li>;
                })}
              </ul>
              {recommendedCourses.length > 5 && (
                <div className="view-more-container text-center mt-2">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => setShowAllCourses(!showAllCourses)}
                  >
                    {showAllCourses
                      ? selectedLanguage === "fil"
                        ? "Tingnan ang Mas Kaunti"
                        : "See Less"
                      : selectedLanguage === "fil"
                        ? "Tingnan ang Higit Pa"
                        : "See More"}
                  </button>
                </div>
              )}
              <p className="small text-muted mt-2 recommendation-note">
                {selectedLanguage === "fil"
                  ? '📌 Mga halimbawang kurso. Para makita ang kumpletong listahan, piliin ang "Yes" sa ibaba. Maaari mo ring pindutin ang kurso sa itaas para sa detalye.'
                  : '📌 Preview  courses. To see the complete list, choose "Yes" below to learn more.'}
              </p>
            </div>
          </div>
        ) : (
          <div className="message bot">
            <div>
              <img
                className="chatbot-avatar message-avatar"
                src={chatbotAvatar}
                alt="chathead"
              />
            </div>
            <div className="message-text bot">
              <img
                className="message-loading"
                src={chatloading}
                alt="loading"
              />
            </div>
          </div>
        )}
      </>
    );
  }
};

export default RecommendedCoursesMessage;
